'use client';

import { useEffect, useRef, useState, PropsWithChildren, Children, isValidElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import debounce from 'lodash/fp/debounce';

import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import CarouselButtons from './carousel-buttons';
import { RoundedButtons } from './carousel-buttons';
import CarouselSlider from './carousel-slider';
import Button from '../button';
import { DRAG_THRESHOLD_PX, suppressNextClick } from './use-drag-to-scroll';

const increment = (n: number) => n + 1;

interface CarouselProps {
	isFullWidth?: boolean;
	className?: string;
	disableButtons?: boolean;
	buttonLabels?: { previous: string; next: string };
	incrementAmount?: number | { sm?: number; md?: number; lg?: number; xl?: number };
	showDots?: boolean;
	showSlider?: boolean;
	sliderLabel?: string;
	buttonsType?: 'rounded' | 'default';
	groupChildrensClassName?: string;
	/** Track gap classes. A prop rather than a `groupChildrensClassName` override because `cn` can't
	 *  dedupe this repo's named spacing tokens against the default, and must stay a bare `gap-*` since
	 *  the scroll math parses the computed `gap`. */
	gapClassName?: string;
	/** Classes for the arrows and slider, which otherwise span the full carousel. For a track that
	 *  bleeds past the content column (`className="spacing-wide-grid-bleed"`), pass
	 *  `spacing-wide-grid-inset` so they stay on the content column. */
	insetClassName?: string;
	adaptToContainerWidth?: boolean;
}

export default function Carousel({
	isFullWidth = false,
	disableButtons = false,
	buttonLabels,
	incrementAmount = 1,
	className,
	showDots = false,
	showSlider = false,
	sliderLabel,
	buttonsType = 'default',
	children,
	groupChildrensClassName,
	gapClassName = 'gap-150 md:gap-300',
	insetClassName,
	adaptToContainerWidth = false,
}: PropsWithChildren<CarouselProps>) {
	const t = useTranslations();
	const labels = buttonLabels ?? { previous: t('Previous slide'), next: t('Next slide') };
	const progressLabel = sliderLabel ?? t('Carousel progress');

	const ref = useRef<HTMLDivElement>(null);
	const widthRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [mediaHeight, setMediaHeight] = useState<number>(0);

	const isMobile = useMediaQuery({ maxWidth: 700 });

	const [forceScrollTo, _setForceScrollTo] = useState(0);
	const setForceScrollTo = () => _setForceScrollTo(increment);

	const [config, setConfig] = useState({
		min: 0,
		max: 0,
		itemGap: 0,
		itemWidth: 0,
		availableWidth: 0,
		active: false,
		incrementAmount: 1,
	});

	const [childIndex, _setChildIndex] = useState(0);

	const setChildIndex = (fn: (n: number) => number) => {
		_setChildIndex(curr => {
			const nextChildIndex = Math.min(Math.max(config.min, fn(curr)), config.max);
			return nextChildIndex;
		});
	};

	function getScrollIndex({
		scrollLeft,
		itemWidth,
		scrollWidth,
	}: {
		scrollLeft: number;
		itemWidth: number;
		scrollWidth: number;
	}) {
		const effectiveWidth = adaptToContainerWidth
			? containerWidth
			: (ref.current?.clientWidth ?? window.innerWidth);

		// The browser clamps scrollLeft to the true scrollable range, which can land
		// short of childIndex * itemWidth (the last item isn't followed by a trailing
		// gap). Snap to the boundary index whenever we're at or past it, for every
		// viewport, so "scroll to the end" doesn't round down to the previous slide.
		if (scrollLeft <= 0) return config.min;
		if (scrollLeft + effectiveWidth >= scrollWidth - 1) return config.max;

		return Math.round(scrollLeft / itemWidth);
	}

	const scrollLeft = useRef(0);
	const isDragging = useRef(false);
	const dragDistance = useRef(0);
	const isProgrammaticScroll = useRef(false);

	const handleScrollLive = () => {
		const element = ref.current;
		if (!element || isProgrammaticScroll.current) return;
		const scrollLeft = element.scrollLeft || 0;
		const scrollWidth = element.scrollWidth || 0;
		const { itemWidth } = config;
		const nextChildIndex = getScrollIndex({ scrollLeft, itemWidth, scrollWidth });
		setChildIndex(() => nextChildIndex);
	};

	const handleScroll = () => {
		const element = ref.current;
		if (!element) return;
		if (isProgrammaticScroll.current) return;
		const scrollLeft = element.scrollLeft || 0;
		const scrollWidth = element.scrollWidth || 0;
		const { itemWidth } = config;
		const nextChildIndex = getScrollIndex({ scrollLeft, itemWidth, scrollWidth });
		setChildIndex(() => nextChildIndex);

		const maxScrollLeft = scrollWidth - element.clientWidth;
		const targetScrollLeft = Math.min(nextChildIndex * itemWidth, maxScrollLeft);
		if (Math.abs(scrollLeft - targetScrollLeft) > 1) {
			setForceScrollTo();
		}
	};
	const debouncedHandleScroll = debounce(200, handleScroll);

	const handleMouseDown = (event: MouseEvent) => {
		const element = ref.current;
		if (!element) return;
		event.preventDefault();
		isDragging.current = true;
		dragDistance.current = 0;
		scrollLeft.current = element.scrollLeft;
		element.addEventListener('mousemove', handleMouseMove);
		element.removeEventListener('scrollend', debouncedHandleScroll);
	};

	const handleMouseMove = (event: MouseEvent) => {
		const element = ref.current;
		if (!element || !isDragging.current) return;
		dragDistance.current += Math.abs(event.movementX);
		element.scrollTo({ left: scrollLeft.current - event.movementX, behavior: 'instant' });
		scrollLeft.current = scrollLeft.current - event.movementX;
	};

	const handleMouseUp = (event: MouseEvent) => {
		const element = ref.current;
		if (!element) return;
		event.preventDefault();
		isDragging.current = false;
		element.removeEventListener('mousemove', handleMouseMove);
		if (dragDistance.current > DRAG_THRESHOLD_PX) suppressNextClick(element);
		debouncedHandleScroll();
		element.addEventListener('scrollend', debouncedHandleScroll);
	};

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		element.addEventListener('mousedown', handleMouseDown);
		element.addEventListener('mouseup', handleMouseUp);
		element.addEventListener('mouseleave', handleMouseUp);
		element.addEventListener('scroll', handleScrollLive);
		element.addEventListener('scrollend', debouncedHandleScroll);

		return () => {
			element.removeEventListener('mousedown', handleMouseDown);
			element.removeEventListener('mousemove', handleMouseMove);
			element.removeEventListener('mouseup', handleMouseUp);
			element.removeEventListener('mouseleave', handleMouseUp);
			element.removeEventListener('scroll', handleScrollLive);
			element.removeEventListener('scrollend', debouncedHandleScroll);
		};
	}, [config.itemWidth, config.itemGap, isMobile]);

	const childCount = Children.count(children);
	const firstChild = Children.toArray(children)[0];
	const firstChildKey = isValidElement(firstChild) ? firstChild.key : null;

	useEffect(() => {
		const element = ref.current;
		if (!element || buttonsType !== 'rounded') return;

		// Consumers mark the media box with data-carousel-media so the rounded buttons centre on it.
		const media = element.querySelector('.carousel-item [data-carousel-media]');
		if (!media) {
			setMediaHeight(0);
			return;
		}

		const measure = () => setMediaHeight(media.getBoundingClientRect().height);
		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(media);
		return () => observer.disconnect();
	}, [buttonsType, childCount, firstChildKey]);

	useEffect(() => {
		const element = ref.current;
		const widthElement = widthRef.current;

		if (!element || !widthElement) return;

		const handleResize = (availableWidth = 0) => {
			const grid = element.querySelector('.grid');
			const itemGap = (grid && parseInt(window.getComputedStyle(grid)?.gap || '0', 10)) || 0;
			const itemWidth = (grid?.firstElementChild?.clientWidth || 0) + itemGap;
			// A bleeding track pads its cards in from the carousel's edges, so only its
			// content box counts towards the cards that fit.
			const trackStyle = window.getComputedStyle(element);
			const trackPadding =
				(parseFloat(trackStyle.paddingLeft) || 0) + (parseFloat(trackStyle.paddingRight) || 0);
			const columns = Math.floor((availableWidth - trackPadding + itemGap) / itemWidth);

			let currentViewportIncrementAmount =
				typeof incrementAmount === 'number' ? incrementAmount : 1;
			if (typeof incrementAmount === 'object') {
				if (window.innerWidth >= 701 && window.innerWidth <= 1100) {
					currentViewportIncrementAmount = incrementAmount['md'] || incrementAmount['sm'] || 1;
				} else if (window.innerWidth >= 1101 && window.innerWidth <= 1700) {
					currentViewportIncrementAmount =
						incrementAmount['lg'] || incrementAmount['md'] || incrementAmount['sm'] || 1;
				} else if (window.innerWidth >= 1701) {
					currentViewportIncrementAmount =
						incrementAmount['xl'] ||
						incrementAmount['lg'] ||
						incrementAmount['md'] ||
						incrementAmount['sm'] ||
						1;
				} else {
					currentViewportIncrementAmount = incrementAmount['sm'] || 1;
				}
			}

			setConfig({
				min: 0,
				max: (grid?.childNodes.length || 0) - columns,
				itemGap,
				itemWidth,
				availableWidth,
				active: true,
				incrementAmount: currentViewportIncrementAmount,
			});
		};

		const debouncedHandleResize = debounce(200, handleResize);

		const resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				const width = entry.contentRect.width;
				setContainerWidth(width);
				debouncedHandleResize(width);
			}
		});

		resizeObserver.observe(widthElement);

		return () => {
			resizeObserver.unobserve(widthElement);
		};
	}, []);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		isProgrammaticScroll.current = true;

		element.style.scrollSnapType = 'none';

		// The active card sits flush against the leading edge with only the next
		// card peeking in, on every viewport (per design) - no centering offset.
		element.scrollTo({
			left: childIndex * config.itemWidth,
			behavior: 'smooth',
		});

		const onScrollSettled = () => {
			element.style.scrollSnapType = '';
			isProgrammaticScroll.current = false;
		};

		element.addEventListener('scrollend', onScrollSettled, { once: true });
		const fallbackTimer = setTimeout(onScrollSettled, 500);

		return () => {
			clearTimeout(fallbackTimer);
			element.removeEventListener('scrollend', onScrollSettled);
		};
	}, [childIndex, config.itemWidth, forceScrollTo]);

	const fullWidthClassNames = [
		'w-screen',
		'px-150',
		'md:px-[calc((100vw-690px)/2)]',
		'lg:px-[calc((100vw-930px)/2)]',
		'xl:px-[calc((100vw-1530px)/2)]',
		'-mx-150',
		'md:mx-[calc((100vw-690px)/-2)]',
		'lg:mx-[calc((100vw-930px)/-2)]',
		'xl:mx-[calc((100vw-1530px)/-2)]',
	].join(' ');

	const sliderPositions = Math.max(config.max - config.min + 1, 1);

	// Fix: Si solo hay un hijo, no renderizar carrusel
	if (Children.count(children) <= 1) {
		return (
			<div className={cn('grid grid-flow-col auto-cols-[285px]', gapClassName)}>{children}</div>
		);
	}

	return (
		<div
			className={cn([
				'relative',
				{ group: buttonsType === 'rounded' },
				{
					flex: buttonsType === 'rounded',
					'flex-col': buttonsType === 'rounded',
					'gap-y-stack-l': buttonsType === 'rounded',
				},
			])}
			ref={widthRef}
		>
			<div
				ref={ref}
				className={cn(
					'w-full no-scrollbar overflow-x-auto',
					'snap-x snap-mandatory md:snap-none touch-pan-x md:touch-auto',
					{
						[fullWidthClassNames]: isFullWidth,
					},
					className
				)}
			>
				<div
					className={cn(
						[
							'grid',
							'grid-flow-col',
							gapClassName,
							'w-max',
							'my-0',
							'mx-auto',
							'auto-cols-[285px]',
							'md:auto-cols-[330px]',
							'lg:auto-cols-[290px]',
							'xl:auto-cols-[490px]',
						],
						groupChildrensClassName
					)}
				>
					{Children.map(children, child => (
						<div className="carousel-item w-full snap-start">{child}</div>
					))}
				</div>
			</div>

			{!disableButtons ? (
				buttonsType === 'default' ? (
					<CarouselButtons
						childIndex={childIndex}
						minChildIndex={config.min}
						maxChildIndex={config.max}
						setChildIndex={setChildIndex}
						incrementAmount={config.incrementAmount}
						labels={labels}
						className={insetClassName}
					/>
				) : buttonsType === 'rounded' ? (
					<RoundedButtons
						childIndex={childIndex}
						minChildIndex={config.min}
						maxChildIndex={config.max}
						setChildIndex={setChildIndex}
						incrementAmount={config.incrementAmount}
						labels={labels}
						className={insetClassName}
						mediaHeight={mediaHeight}
					/>
				) : null
			) : null}

			{showDots ? (
				<div className="flex items-center justify-center gap-2">
					{Array.from({ length: Children.count(children) }).map((_, index) => (
						<Button
							intent="unstyled"
							key={index}
							onClick={() => setChildIndex(() => index)}
							className={cn([
								'cursor-pointer',
								'rounded-full',
								'transition-colors',
								{
									'bg-text-disabled': index !== childIndex,
									'bg-black': index === childIndex,
									'w-[7px] h-[7px]': index < 2,
									'w-[6px] h-[6px]': index === 2,
									'w-[5px] h-[5px]': index > 2,
								},
							])}
							aria-label={t('Go to slide {0}', [index + 1])}
						/>
					))}
				</div>
			) : null}

			{showSlider ? (
				<div
					role="progressbar"
					className={insetClassName}
					aria-label={progressLabel}
					aria-valuemin={1}
					aria-valuenow={childIndex - config.min + 1}
					aria-valuemax={sliderPositions}
				>
					<CarouselSlider
						widthPct={100 / sliderPositions}
						offsetPct={((childIndex - config.min) * 100) / sliderPositions}
					/>
				</div>
			) : null}
		</div>
	);
}
